import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";
import { ArrowLeft, Bookmark, ChevronLeft, ChevronRight, List, Type, X } from "lucide-react";
import { BOOK_PAGES } from "../data/bookData";

const THEMES = ["parchment", "blush", "velvet"];
const SIZES = [
  { label: "S", cls: "text-sm leading-relaxed" },
  { label: "M", cls: "text-base leading-relaxed" },
  { label: "L", cls: "text-lg leading-loose" },
  { label: "XL", cls: "text-xl leading-loose" },
];
const BOOKMARK_KEY = "keepsake-bookmark";
const TOTAL = BOOK_PAGES.length;

// One handwritten subtitle per chapter, shown on its opening page (1-indexed).
const PAGE_SUBTITLES = {
  1: "read this first, auntie",
  3: "freshman year — the text that started everything",
  7: "the year we found our rhythm, and almost lost it",
  10: "the night i got it wrong, and the night you made it right",
  13: "four months of nothing",
  15: "april 18th — the poster",
  17: "two years, compressed into a few pages",
};

const baseTitle = (p) => p.title?.split(" — ")[0] ?? p.title;
const CHAPTER_PAGES = BOOK_PAGES
  .map((p, i) => ({ ...p, page: i + 1 }))
  .filter((p, i, arr) => p.title && arr.findIndex((q) => baseTitle(q) === baseTitle(p)) === i);

export default function Reader() {
  const [params, setParams] = useSearchParams();
  const initial = Math.min(Math.max(parseInt(params.get("page") || "1", 10) || 1, 1), TOTAL);
  const [page, setPage] = useState(initial);
  const [dir, setDir] = useState(1);
  const [theme, setTheme] = useState("parchment");
  const [size, setSize] = useState(1);
  const [tocOpen, setTocOpen] = useState(false);
  const [bookmark, setBookmark] = useState(() => Number(localStorage.getItem(BOOKMARK_KEY)) || null);
  const [celebrated, setCelebrated] = useState(false);

  useEffect(() => {
    document.title = `Page ${page} of ${TOTAL} — the story of you and i`;
  }, [page]);

  const go = useCallback((next) => {
    const clamped = Math.min(Math.max(next, 1), TOTAL);
    setDir(clamped > page ? 1 : -1);
    setPage(clamped);
    setParams({ page: String(clamped) }, { replace: true });
  }, [page, setParams]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(page + 1);
      if (e.key === "ArrowLeft") go(page - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, page]);

  useEffect(() => {
    if (page === TOTAL && !celebrated) {
      setCelebrated(true);
      confetti({
        particleCount: 160,
        spread: 80,
        origin: { y: 0.7 },
        colors: ["#E8B4B8", "#C87D87", "#D6CBEB", "#D4A359", "#FAE3E8"],
      });
    }
  }, [page, celebrated]);

  const toggleBookmark = () => {
    const next = bookmark === page ? null : page;
    setBookmark(next);
    if (next) localStorage.setItem(BOOKMARK_KEY, String(next));
    else localStorage.removeItem(BOOKMARK_KEY);
  };

  const current = BOOK_PAGES[page - 1];
  const progress = useMemo(() => Math.round((page / TOTAL) * 100), [page]);
  const dark = theme === "velvet";

  return (
    <div data-testid="book-reader-container" className={`reader-${theme} flex min-h-screen flex-col transition-colors duration-500`}>
      {/* top bar */}
      <div className={`sticky top-0 z-40 border-b backdrop-blur-md ${dark ? "border-white/10 bg-[#1F1518]/80" : "border-[#E8B4B8]/40 bg-[#FAF6EE]/80"}`}>
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link to="/" data-testid="reader-back-link" aria-label="Back to home"
            className={`rounded-full p-2 transition-colors ${dark ? "text-[#E9DCE0]/70 hover:bg-white/10" : "text-[#4A2E35]/70 hover:bg-[#FAE3E8]"}`}>
            <ArrowLeft size={18} />
          </Link>
          <div className="flex-1 px-2">
            <div className="flex items-center justify-between text-[11px] font-semibold">
              <span className={dark ? "reader-muted" : "text-[#4A2E35]/60"}>Page {page} of {TOTAL}</span>
              <span className={dark ? "reader-muted" : "text-[#4A2E35]/60"}>{progress}%</span>
            </div>
            <div className={`mt-1.5 h-1.5 overflow-hidden rounded-full ${dark ? "bg-white/10" : "bg-[#E8B4B8]/30"}`}>
              <div data-testid="reader-progress-bar" className="h-full rounded-full bg-[#C87D87] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="reader-toc-drawer-btn" onClick={() => setTocOpen(true)} aria-label="Table of contents"
              className={`rounded-full p-2 transition-colors ${dark ? "text-[#E9DCE0]/70 hover:bg-white/10" : "text-[#4A2E35]/70 hover:bg-[#FAE3E8]"}`}>
              <List size={17} />
            </button>
            <button data-testid="reader-bookmark-btn" onClick={toggleBookmark} aria-label="Bookmark this page"
              className={`rounded-full p-2 transition-colors ${bookmark === page ? "text-[#D4A359]" : dark ? "text-[#E9DCE0]/70 hover:bg-white/10" : "text-[#4A2E35]/70 hover:bg-[#FAE3E8]"}`}>
              <Bookmark size={17} fill={bookmark === page ? "currentColor" : "none"} />
            </button>
          </div>
        </div>
      </div>

      {/* page */}
      <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6">
        <div className="relative w-full max-w-2xl">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.article
              key={page}
              custom={dir}
              initial={{ opacity: 0, x: dir * 60, rotateY: dir * 6 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              exit={{ opacity: 0, x: dir * -60, rotateY: dir * -6 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="reader-page paper-card deckle px-7 py-12 sm:px-14 sm:py-16"
              data-testid="reader-page"
            >
              <p className={`text-center font-display text-sm italic ${dark ? "reader-muted" : "text-[#C87D87]"}`}>
                ~ {String(page).padStart(2, "0")} ~
              </p>
              {page !== TOTAL && current.title &&
                BOOK_PAGES.findIndex((q) => q.title && baseTitle(q) === baseTitle(current)) === page - 1 && (
                <h1 className="mt-4 text-center font-display text-3xl font-semibold sm:text-4xl">{baseTitle(current)}</h1>
              )}
              {PAGE_SUBTITLES[page] && (
                <p data-testid="reader-chapter-subtitle" className="mt-3 text-center font-hand text-xl text-[#C87D87] sm:text-2xl">
                  {PAGE_SUBTITLES[page]}
                </p>
              )}
              <div className={`mx-auto mt-6 h-px w-16 ${dark ? "bg-white/20" : "bg-[#E8B4B8]"}`} />
              {current.toc ? (
                <div className="mt-8 space-y-1 font-display" data-testid="book-contents-list">
                  {CHAPTER_PAGES.filter((c) => !c.toc).map((c) => (
                    <button
                      key={c.page}
                      data-testid={`contents-jump-${c.page}`}
                      onClick={() => go(c.page)}
                      className="group flex w-full items-baseline gap-4 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[#FAE3E8]/50"
                    >
                      <span className="flex-1 text-lg sm:text-xl">{baseTitle(c)}</span>
                      <span className="text-sm italic text-[#C87D87] transition-transform group-hover:translate-x-1">p. {c.page}</span>
                    </button>
                  ))}
                </div>
              ) : page === TOTAL ? (
                <div className="mt-6 flex flex-col items-center">
                  <div className="polaroid" style={{ transform: "rotate(-2deg)" }}>
                    <span className="tape" />
                    <img src="/photos/the-card.webp" alt="The handmade card Tina made for Jayden" className="h-44 object-contain sm:h-56" />
                  </div>
                  <p className="mt-8 text-center font-display text-5xl font-semibold italic leading-snug sm:text-7xl" data-testid="reader-final-line">
                    {current.text}
                  </p>
                </div>
              ) : (
                <div className={`mt-8 space-y-5 font-display ${SIZES[size].cls}`}>
                  {current.text.split(/\n\s*\n/).map((para, i) => (
                    <p key={i} className={i === 0 ? "first-letter:float-left first-letter:mr-2 first-letter:font-display first-letter:text-5xl first-letter:font-semibold first-letter:leading-[0.85] first-letter:text-[#C87D87]" : ""}>
                      {para}
                    </p>
                  ))}
                </div>
              )}
              {page === TOTAL && (
                <p className="mt-10 text-center font-hand text-2xl text-[#C87D87]">the end!</p>
              )}
            </motion.article>
          </AnimatePresence>
        </div>
      </main>

      {/* bottom controls */}
      <div className="sticky bottom-0 z-40 px-4 pb-5">
        <div className={`mx-auto flex max-w-2xl flex-wrap items-center justify-center gap-3 rounded-full border px-4 py-3 shadow-lg backdrop-blur-md ${dark ? "border-white/10 bg-[#2A1C22]/90" : "border-[#E8B4B8]/40 bg-[#FFFDF9]/90"}`}>
          <button data-testid="reader-prev-page-btn" onClick={() => go(page - 1)} disabled={page === 1} aria-label="Previous page"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#C87D87] px-5 py-2.5 text-xs font-semibold text-[#FFFDF9] transition-all hover:scale-105 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:scale-100">
            <ChevronLeft size={15} /> Prev
          </button>
          <button data-testid="reader-next-page-btn" onClick={() => go(page + 1)} disabled={page === TOTAL} aria-label="Next page"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#C87D87] px-5 py-2.5 text-xs font-semibold text-[#FFFDF9] transition-all hover:scale-105 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:scale-100">
            Next <ChevronRight size={15} />
          </button>

          <div className={`flex items-center gap-1 rounded-full px-2 py-1 ${dark ? "bg-white/10" : "bg-[#FAF6EE]"}`} data-testid="reader-font-size-toggle">
            <Type size={13} className={dark ? "reader-muted" : "text-[#4A2E35]/50"} />
            {SIZES.map((s, i) => (
              <button key={s.label} data-testid={`font-size-${s.label.toLowerCase()}`} onClick={() => setSize(i)} aria-label={`Font size ${s.label}`}
                className={`rounded-full px-2 py-1 text-[11px] font-semibold transition-colors ${size === i ? "bg-[#C87D87] text-[#FFFDF9]" : dark ? "text-[#E9DCE0]/70" : "text-[#4A2E35]/60 hover:text-[#4A2E35]"}`}>
                {s.label}
              </button>
            ))}
          </div>

          <div className={`flex items-center gap-1 rounded-full px-2 py-1 ${dark ? "bg-white/10" : "bg-[#FAF6EE]"}`} data-testid="reader-theme-toggle">
            {THEMES.map((t) => (
              <button key={t} data-testid={`theme-${t}`} onClick={() => setTheme(t)} aria-label={`${t} theme`}
                className={`h-6 w-6 rounded-full border-2 transition-transform ${theme === t ? "scale-110 border-[#C87D87]" : "border-transparent"}`}
                style={{ backgroundColor: t === "parchment" ? "#FAF6EE" : t === "blush" ? "#FAE3E8" : "#2A1C22", boxShadow: "inset 0 0 0 1px rgba(74,46,53,0.15)" }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* TOC drawer */}
      <AnimatePresence>
        {tocOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-[#362428]/50 backdrop-blur-sm" onClick={() => setTocOpen(false)} />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              className="fixed bottom-0 right-0 top-0 z-50 flex w-80 max-w-[85vw] flex-col bg-[#FFFDF9] shadow-2xl"
              data-testid="reader-toc-drawer"
            >
              <div className="flex items-center justify-between border-b border-[#E8B4B8]/30 px-6 py-5">
                <h2 className="font-display text-xl font-semibold text-[#4A2E35]">Contents</h2>
                <button data-testid="toc-close-btn" onClick={() => setTocOpen(false)} aria-label="Close contents"
                  className="rounded-full p-2 text-[#4A2E35]/60 hover:bg-[#FAE3E8]">
                  <X size={17} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {CHAPTER_PAGES.map((c) => (
                  <button key={c.page} data-testid={`toc-jump-${c.page}`}
                    onClick={() => { go(c.page); setTocOpen(false); }}
                    className={`flex w-full items-baseline gap-3 px-6 py-3.5 text-left transition-colors hover:bg-[#FAE3E8]/50 ${page === c.page ? "bg-[#FAE3E8]/60" : ""}`}>
                    <span className="font-display text-xs italic text-[#C87D87]">p. {c.page}</span>
                    <span className={`flex-1 text-sm ${page === c.page ? "font-semibold text-[#4A2E35]" : "text-[#4A2E35]/75"}`}>{baseTitle(c)}</span>
                    {bookmark === c.page && <Bookmark size={12} className="text-[#D4A359]" fill="currentColor" />}
                  </button>
                ))}
              </div>
              {bookmark && (
                <button data-testid="toc-resume-bookmark-btn" onClick={() => { go(bookmark); setTocOpen(false); }}
                  className="border-t border-[#E8B4B8]/30 px-6 py-4 text-center text-xs font-semibold text-[#C87D87] hover:bg-[#FAE3E8]/40">
                  Resume at bookmarked page {bookmark}
                </button>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

    </div>
  );
}
